import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  getAdminMobileVerificationSettings,
  updateAdminMobileVerificationSettings,
  getSubscriptionSettings,
  updateSubscriptionSettings
} from "../../services/adminService";

function Settings() {
  const [settings,    setSettings]    = useState({ otpEnabled: true, otpMandatory: false });
  const [subConfig,   setSubConfig]   = useState({ subscriptionPriceINR: 100, trialDays: 90 });
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [savingSub,   setSavingSub]   = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [otpRes, subRes] = await Promise.all([
        getAdminMobileVerificationSettings(),
        getSubscriptionSettings()
      ]);
      setSettings(otpRes.data);
      setSubConfig(subRes.data);
    } catch {
      toast.error("Failed to load settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpEnabledChange = (e) => {
    const enabled = e.target.checked;
    setSettings((prev) => ({
      ...prev,
      otpEnabled: enabled,
      // If OTP is turned off, mandatory must also be off
      otpMandatory: enabled ? prev.otpMandatory : false
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await updateAdminMobileVerificationSettings(settings);
      setSettings(res.data);
      toast.success("Settings saved successfully.");
    } catch {
      toast.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubSave = async () => {
    const price = Number(subConfig.subscriptionPriceINR);
    const days  = Number(subConfig.trialDays);
    if (!price || price < 1)          { toast.error("Price must be at least ₹1.");        return; }
    if (!days  || days < 1 || days > 365) { toast.error("Trial days must be between 1 and 365."); return; }
    try {
      setSavingSub(true);
      const res = await updateSubscriptionSettings({ subscriptionPriceINR: price, trialDays: days });
      setSubConfig(res.data);
      toast.success("Subscription settings saved.");
    } catch {
      toast.error("Failed to save subscription settings.");
    } finally {
      setSavingSub(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h2>Settings</h2>
        <p style={{ color: "#5f6368" }}>Loading settings...</p>
      </div>
    );
  }

  return (
    <div>
      <h2>Settings</h2>

      <div className="admin-card settings-section">
        <h3 className="settings-section-title">Mobile Verification</h3>
        <p className="settings-section-desc">
          Control how OTP verification works during business registration.
        </p>

        {/* Toggle: Enable OTP */}
        <div className="settings-toggle-row">
          <label className="settings-toggle-label">
            <input
              type="checkbox"
              checked={settings.otpEnabled}
              onChange={handleOtpEnabledChange}
            />
            <span>Enable OTP Verification</span>
          </label>
          <p className="settings-toggle-hint">
            Shows the "Verify Mobile" button on the registration form.
          </p>
        </div>

        {/* Toggle: Mandatory OTP — disabled when OTP is off */}
        <div className={`settings-toggle-row${!settings.otpEnabled ? " settings-row-disabled" : ""}`}>
          <label className="settings-toggle-label">
            <input
              type="checkbox"
              checked={settings.otpMandatory}
              disabled={!settings.otpEnabled}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, otpMandatory: e.target.checked }))
              }
            />
            <span>Require OTP Verification Before Registration</span>
          </label>
          <p className="settings-toggle-hint">
            Blocks form submission until the mobile number is verified.
          </p>
        </div>

        {/* Current behaviour summary */}
        <div className="settings-status-box">
          {!settings.otpEnabled && (
            <p>OTP verification is <strong>disabled</strong>. The Verify Mobile button will not appear on the registration form.</p>
          )}
          {settings.otpEnabled && !settings.otpMandatory && (
            <p>OTP verification is <strong>optional</strong>. Businesses can register without verifying their mobile number.</p>
          )}
          {settings.otpEnabled && settings.otpMandatory && (
            <p>OTP verification is <strong>mandatory</strong>. Businesses must verify their mobile number before submitting the form.</p>
          )}
        </div>

        <button
          className="settings-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      {/* ── Subscription Config ── */}
      <div className="admin-card settings-section" style={{ marginTop: 24 }}>
        <h3 className="settings-section-title">Subscription Pricing</h3>
        <p className="settings-section-desc">
          Changes apply to new businesses and new payment orders only.
          Existing trials and paid periods are never affected retroactively.
        </p>

        <div className="settings-field-row">
          <label className="settings-field-label">
            Annual Plan Price (₹)
            <p className="settings-toggle-hint">Charged per listing per year via Razorpay.</p>
          </label>
          <input
            type="number"
            min="1"
            className="settings-number-input"
            value={subConfig.subscriptionPriceINR}
            onChange={(e) =>
              setSubConfig((p) => ({ ...p, subscriptionPriceINR: e.target.value }))
            }
          />
        </div>

        <div className="settings-field-row" style={{ marginTop: 16 }}>
          <label className="settings-field-label">
            Free Trial Period (days)
            <p className="settings-toggle-hint">
              Applied to each new business at registration. Max 365.
            </p>
          </label>
          <input
            type="number"
            min="1"
            max="365"
            className="settings-number-input"
            value={subConfig.trialDays}
            onChange={(e) =>
              setSubConfig((p) => ({ ...p, trialDays: e.target.value }))
            }
          />
        </div>

        <button
          className="settings-save-btn"
          onClick={handleSubSave}
          disabled={savingSub}
          style={{ marginTop: 20 }}
        >
          {savingSub ? "Saving..." : "Save Subscription Settings"}
        </button>
      </div>
    </div>
  );
}

export default Settings;
